import { CheckCircle2, AlertTriangle, MinusCircle, HelpCircle, ShieldAlert, CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";

const fmtFrom = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "a later date");

export const SCOPE = {
  in_scope: { label: "In scope", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2, color: "#059669" },
  check: { label: "Check", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle, color: "#D97706" },
  out_of_scope: { label: "Out of scope", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: MinusCircle, color: "#94A3B8" },
  missing_code: { label: "Missing code", cls: "bg-rose-50 text-rose-700 border-rose-200", icon: HelpCircle, color: "#E11D48" },
  not_required: { label: "Not required", cls: "bg-zinc-100 text-zinc-500 border-zinc-300 line-through decoration-zinc-400", icon: MinusCircle, color: "#52525B" },
  future: { label: "In scope from 30 Dec 2027", cls: "bg-sky-50 text-sky-800 border-sky-200", icon: CalendarClock, color: "#0284C7" },
};

export function ScopeBadge({ scope, overridden, testid, from }) {
  const s = SCOPE[scope] || SCOPE.out_of_scope;
  const Icon = s.icon;
  return (
    <span
      data-testid={testid || `scope-badge-${scope}`}
      className={cn("inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap", s.cls)}
    >
      <Icon className="h-3.5 w-3.5" />
      {scope === "future" && from ? `In scope from ${fmtFrom(from)}` : s.label}
      {overridden && <ShieldAlert className="h-3.5 w-3.5 opacity-70" aria-label="overridden" />}
    </span>
  );
}

const STATUS = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed_with_errors: "bg-amber-50 text-amber-800 border-amber-200",
  processing: "bg-sky-50 text-sky-700 border-sky-200",
  queued: "bg-slate-100 text-slate-600 border-slate-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  revoked: "bg-slate-100 text-slate-500 border-slate-200",
  overdue: "bg-rose-50 text-rose-700 border-rose-300",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  setup: "bg-amber-50 text-amber-800 border-amber-200",
  preview_ready: "bg-violet-50 text-violet-700 border-violet-200",
  applying: "bg-sky-50 text-sky-700 border-sky-200",
};

export function StatusChip({ status, label, testid }) {
  return (
    <span
      data-testid={testid}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap",
        STATUS[status] || STATUS.queued
      )}
    >
      {(status === "processing" || status === "queued") && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
      {label || String(status || "").replaceAll("_", " ")}
    </span>
  );
}

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4 fade-up" data-testid={testid}>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-sm text-slate-500">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function Panel({ title, subtitle, action, children, className, testid }) {
  return (
    <section data-testid={testid} className={cn("rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]", className)}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="text-[15px] font-semibold text-slate-900">{title}</h3>
            {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function KpiCard({ label, value, sub, icon: Icon, tone = "slate", testid, onClick }) {
  const tones = {
    green: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
    slate: "text-slate-700 bg-slate-100",
    brand: "text-[#D52B3B] bg-[#D52B3B]/10",
    rose: "text-rose-700 bg-rose-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && (
          <span className={cn("rounded-lg p-2", tones[tone])}>
            <Icon className="h-4 w-4" />
          </span>
        )}
      </div>
      <p className="mt-3 font-display text-3xl font-semibold tracking-tight text-slate-900" data-testid={testid && `${testid}-value`}>
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </button>
  );
}

export function Empty({ title, text, icon: Icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      {Icon && <Icon className="mb-3 h-9 w-9 text-slate-300" />}
      <p className="font-medium text-slate-700">{title}</p>
      {text && <p className="mt-1 max-w-sm text-sm text-slate-500">{text}</p>}
      {children}
    </div>
  );
}
